// VirtualRoomProximityHelper.ts
// A lightweight utility that encapsulates communication eligibility logic
// between two players taking VirtualRoom areas into account.
//
// 1. If BOTH players are inside the same VirtualRoomArea, they may communicate
//    only if their distance is <= area.properties.proximityDistance (or the
//    provided fallbackDistance if proximityDistance is undefined; see
//    DEFAULT_PROXIMITY_FALLBACK_PX at call sites).
// 2. If NEITHER player is inside any VirtualRoomArea, they may communicate when
//    their distance is <= fallbackDistance (the existing proximity radius).
// 3. If players are in DIFFERENT areas or only one player is in an area, they
//    may NOT communicate.
//
// The helper is completely stateless and therefore easy to reuse / extend.
// Additional rules can be layered on top without touching call-sites.

import { virtualRoomAreaService, VirtualRoomArea } from './VirtualRoomAreaService'

export interface PlayerPosition {
  x: number
  y: number
  playerId: string
}

/**
 * Calculate the Euclidean distance between two points.
 */
function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Faster squared distance for comparison without costly square-root.
 * Useful when only relative distance matters.
 */
export function distanceSquared(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return dx * dx + dy * dy
}

/**
 * Determine whether two players should be able to exchange media.
 *
 * @param a               First player position
 * @param b               Second player position
 * @param fallbackDistance Distance (in px) used when neither player is inside any room
 * @returns true if players are allowed to communicate, otherwise false
 */
export function canPlayersCommunicate(
  a: PlayerPosition,
  b: PlayerPosition,
  fallbackDistance: number
): boolean {
  // When service not initialized or no current room, treat both as "outside areas" - use fallback distance
  if (!virtualRoomAreaService.isInitialized() || !virtualRoomAreaService.getState().currentCustomRoomId) {
    return distance(a, b) <= fallbackDistance
  }

  // Resolve areas for both players via the singleton service
  const areaA: VirtualRoomArea | null = virtualRoomAreaService.isPlayerInArea(a.x, a.y)
  const areaB: VirtualRoomArea | null = virtualRoomAreaService.isPlayerInArea(b.x, b.y)

  // CASE 1: both players are located within a virtual room area
  if (areaA && areaB) {
    // Only allow when they share the SAME room
    if (areaA.roomId !== areaB.roomId) return false

    // Per-room override: unset, <= 0, or Infinity = full conference (no distance gate).
    const rawThreshold = areaA.properties?.proximityDistance
    if (
      rawThreshold === undefined ||
      rawThreshold === null ||
      rawThreshold <= 0 ||
      !Number.isFinite(rawThreshold)
    ) {
      return true
    }

    return distance(a, b) <= rawThreshold
  }

  // CASE 2: neither player is inside a room → fallback to default proximity radius
  if (!areaA && !areaB) {
    return distance(a, b) <= fallbackDistance
  }

  // CASE 3: players are in mismatching contexts (one inside a room or different rooms)
  return false
} 